import { distinct, type Chart } from '../../lib/grammar/charts';

// ─────────────────────────────────────────────────────────────────────────────
// The Sunken Fortress, generated: three floors of Zelda-style rooms, the same
// for the whole class all week (seeded by class + week). Each floor's last room
// is behind a locked door, and the key is in a side room. Rooms come in
// different shapes, some with hills and ramps, and floors are joined by real
// stairs. The puzzles are built from the class's verb charts (lib/grammar/charts.ts):
//   verb      push stone blocks carved with a verb's forms into the holes of
//             their pronouns (a wrong hole spits the block back, with a crab)
//   plates    the tablet calls pronouns in order; step on each one's form
//   crystals  the tablet shows a pronoun; strike the crystal with its form
//   mirror    turn the mirrors so the lantern's light lands on the right form
//   sun       shoot an arrow through the sunbeam into the right form's sun plaque
//   boss      the Kraken shouts a form; strike its pronoun's crystal to stun it
// Eye sentries (Beamos-style) stand still and fire lasers wherever you are.
// Units are tiles. A room is RW × RH tiles with its walls; (0,0) is the
// north-west wall corner, y grows south. Heights are in steps (one step = one
// ramp's rise); a ramp's `ramp` direction is the way it goes UP.
// ─────────────────────────────────────────────────────────────────────────────

export const RW = 15, RH = 11;
export const FLOORS = 3;
export type Dir = 'n' | 's' | 'e' | 'w';
export const DIRS: Dir[] = ['n', 's', 'e', 'w'];
export const OPP: Record<Dir, Dir> = { n: 's', s: 'n', e: 'w', w: 'e' };
export const STEP: Record<Dir, [number, number]> = { n: [0, -1], s: [0, 1], e: [1, 0], w: [-1, 0] };
/** The door tile in each wall. */
export const DOOR_AT: Record<Dir, [number, number]> = { n: [7, 0], s: [7, RH - 1], w: [0, 5], e: [RW - 1, 5] };
/** Where the player stands at the start of a floor (at the foot of the stairs they came down). */
export const SPAWN: [number, number] = [7.5, 6.5];

export type RoomKind = 'start' | 'combat' | 'verb' | 'plates' | 'crystals' | 'mirror' | 'sun' | 'key' | 'treasure' | 'stairs' | 'boss';
export type EnemyKind = 'crab' | 'jelly' | 'puffer' | 'urchin' | 'eye';
export type Item = 'key' | 'heart' | 'crown';
export type DecorKind = 'kelp' | 'coral' | 'shell' | 'rock' | 'anemone';
export type Shape = 'rect' | 'bevel' | 'cut' | 'cross' | 'octa' | 'core' | 'hall';

/** One question: show `who`, the answer is `form`, choose among `options`. No chart: shapes. */
export type Round = { chart: Chart | null; who: string; form: string; options: string[] };
export type VerbPuzzle = {
  /** null: no verbs to drill (the class hasn't any), so shapes instead of words. */
  chart: Chart | null;
  holes: { x: number; y: number; label: string; key: string }[];
  blocks: { x: number; y: number; label: string; key: string }[];
};
export type PlatesPuzzle = { chart: Chart; order: { who: string; form: string }[]; plates: { x: number; y: number; form: string }[] };
export type CrystalPuzzle = { rounds: Round[]; spots: [number, number][] };
/** A lantern shines east along a row; mirrors on that row turn it north or south (or let it pass) onto receivers. */
export type MirrorPuzzle = { source: [number, number]; mirrors: { x: number; y: number; state: number }[]; receivers: [number, number][]; rounds: Round[] };
/** A sunbeam falls from the ceiling; arrows shot through it light up; sun plaques hang on the north wall. */
export type SunPuzzle = { shaft: [number, number]; plaques: number[]; rounds: Round[] };
export type BossFight = { charts: Chart[]; spots: [number, number][] };

export type Room = {
  id: number; gx: number; gy: number; kind: RoomKind; shape: Shape;
  doors: Partial<Record<Dir, { to: number; locked: boolean }>>;
  /** Per tile (index y * RW + x): solid rock (the outer wall, and the room's own shape). */
  wall: boolean[];
  /** Per tile: the floor's height in steps (the low end, on a ramp). */
  h: number[];
  /** Per tile: a ramp going up this way, or null. */
  ramp: (Dir | null)[];
  enemies: { kind: EnemyKind; x: number; y: number }[];
  /** Solid rock pillars. */
  pillars: [number, number][];
  /** Clam pots: strike them for a heart now and then. */
  pots: [number, number][];
  decor: { kind: DecorKind; x: number; y: number; s: number; r: number }[];
  /** `hidden` chests appear once the room is solved. */
  chest: { x: number; y: number; item: Item; hidden: boolean } | null;
  /** The start of floors 2 and 3: the stairs you came down, against the south wall. */
  stairsUp: boolean;
  verb?: VerbPuzzle;
  plates?: PlatesPuzzle;
  crystals?: CrystalPuzzle;
  mirror?: MirrorPuzzle;
  sun?: SunPuzzle;
  boss?: BossFight;
};
export type Theme = { name: string; floor: number; wall: number; trim: number; fog: number; light: number };
export type Floor = { n: number; theme: Theme; rooms: Room[]; start: number };

export const THEMES: Theme[] = [
  { name: 'The Coral Halls', floor: 0xd9c49a, wall: 0x6f8f9a, trim: 0xff8a7a, fog: 0x0d4a6b, light: 0xbff3ff },
  { name: 'The Kelp Catacombs', floor: 0xa8b48a, wall: 0x4f6a5a, trim: 0x8fe08a, fog: 0x0b3a4a, light: 0xc8ffd8 },
  { name: 'The Kraken’s Vault', floor: 0x8f86a8, wall: 0x3e3a5c, trim: 0xc08aff, fog: 0x120d33, light: 0xd8c8ff },
];

export function towerSeed(classroomId: string, weekStart: string): number {
  let h = 2166136261;
  for (const ch of `${classroomId}|${weekStart}|tower3d`) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/** A small seeded random-number generator (mulberry32). */
export function rng(seed: number) {
  let a = seed >>> 0;
  const next = () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const int = (lo: number, hi: number) => lo + Math.floor(next() * (hi - lo + 1));
  const pick = <T,>(a: readonly T[]) => a[Math.floor(next() * a.length)];
  const shuffle = <T,>(a: readonly T[]) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(next() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };
  return { next, int, pick, shuffle };
}
type Rng = ReturnType<typeof rng>;

export const SHAPES = ['🐚', '⭐', '🌙', '💧', '🔺', '❤'];
const k = (x: number, y: number) => `${x},${y}`;
export const idx = (x: number, y: number) => y * RW + x;
/** The floor tile just inside each door, where you arrive. */
export const ENTRY: Record<Dir, [number, number]> = { n: [7, 1], s: [7, RH - 2], w: [1, 5], e: [RW - 2, 5] };

// ── Moving between tiles ────────────────────────────────────────────────────
// Shared by the generator's checks and the game itself, so they always agree.

export function isWall(room: Room, x: number, y: number): boolean {
  return x < 0 || y < 0 || x >= RW || y >= RH || room.wall[idx(x, y)];
}
/** The floor's height on one side of a tile: a ramp is low at the bottom, high at the top, and can't be stepped onto from the side (null). */
export function edgeH(room: Room, x: number, y: number, side: Dir): number | null {
  if (x < 0 || y < 0 || x >= RW || y >= RH) return 0;
  const i = idx(x, y), h = room.h[i], up = room.ramp[i];
  if (!up) return h;
  if (side === up) return h + 1;
  if (side === OPP[up]) return h;
  return null;
}
/** The floor's height (in steps) at a point. */
export function groundAt(room: Room, x: number, y: number): number {
  const tx = Math.floor(x), ty = Math.floor(y);
  if (tx < 0 || ty < 0 || tx >= RW || ty >= RH) return 0;
  const i = idx(tx, ty), h = room.h[i], up = room.ramp[i];
  if (!up) return h;
  const f = up === 'n' ? ty + 1 - y : up === 's' ? y - ty : up === 'e' ? x - tx : tx + 1 - x;
  return h + Math.min(1, Math.max(0, f));
}
/** The highest the floor gets on a tile (what a laser or an arrow has to clear). */
export const tileTop = (room: Room, x: number, y: number) => (x < 0 || y < 0 || x >= RW || y >= RH ? 0 : room.h[idx(x, y)] + (room.ramp[idx(x, y)] ? 1 : 0));
/** Can you step from tile (x, y) to its neighbour in direction d? `drop`: jumping down off a ledge is allowed (the player can; enemies can't). */
export function canStep(room: Room, x: number, y: number, d: Dir, drop: boolean): boolean {
  const [dx, dy] = STEP[d], nx = x + dx, ny = y + dy;
  if (isWall(room, nx, ny)) return false;
  const a = edgeH(room, x, y, d), b = edgeH(room, nx, ny, OPP[d]);
  if (a == null || b == null) return false;
  return Math.abs(a - b) < 0.4 || (drop && a > b);
}

// ── A floor ─────────────────────────────────────────────────────────────────

export function makeFloor(seed: number, n: number, charts: Chart[]): Floor {
  for (let attempt = 0; ; attempt++) {
    const f = tryFloor(rng(seed + n * 7919 + attempt * 104729), n, charts);
    if (f) return f;
  }
}

function tryFloor(r: Rng, n: number, charts: Chart[]): Floor | null {
  // The main path: a walk on a 5×5 grid of rooms, from the entrance to the goal.
  const length = 5 + n;
  const at = new Map<string, number>();
  const cells: [number, number][] = [[2, 4]];
  at.set(k(2, 4), 0);
  while (cells.length < length) {
    const [x, y] = cells[cells.length - 1];
    const next = r.shuffle(DIRS.filter(d => d !== 's')).map(d => [x + STEP[d][0], y + STEP[d][1]] as [number, number])
      .filter(([a, b]) => a >= 0 && a < 5 && b >= 0 && b < 5 && !at.has(k(a, b)));
    if (!next.length) return null;
    at.set(k(...next[0]), cells.length); cells.push(next[0]);
  }
  const links: [number, number][] = cells.slice(1).map((_, i) => [i, i + 1]);
  // Side rooms off the path (never off the goal or the room before it): the key, a treasure.
  const side = (from: number): number | null => {
    const [x, y] = cells[from];
    for (const d of r.shuffle(DIRS)) {
      const a = x + STEP[d][0], b = y + STEP[d][1];
      if (a < 0 || a >= 5 || b < 0 || b >= 5 || at.has(k(a, b))) continue;
      at.set(k(a, b), cells.length); cells.push([a, b]); links.push([from, cells.length - 1]);
      return cells.length - 1;
    }
    return null;
  };
  const keyRoom = side(r.int(1, length - 3));
  const treasure = side(r.int(0, length - 3));
  if (keyRoom == null || treasure == null) return null;
  const goal = length - 1;

  // What each room is. The path between the entrance and the goal is mostly puzzles.
  const kinds: RoomKind[] = cells.map(() => 'combat');
  kinds[0] = 'start'; kinds[goal] = n === FLOORS ? 'boss' : 'stairs';
  kinds[keyRoom] = 'key'; kinds[treasure] = 'treasure';
  const usable = charts.length > 0;
  const middle = r.shuffle(Array.from({ length: goal - 1 }, (_, i) => i + 1));
  const PUZZLES: RoomKind[][] = usable
    ? [['verb', 'mirror', 'crystals', 'verb'], ['verb', 'sun', 'plates', 'mirror', 'verb'], ['verb', 'mirror', 'sun', 'verb', 'plates']]
    : [['verb', 'mirror', 'combat', 'verb'], ['verb', 'sun', 'mirror', 'verb'], ['verb', 'mirror', 'sun', 'verb']];
  const puzzles = PUZZLES[n - 1];
  middle.forEach((id, i) => { kinds[id] = puzzles[i] ?? 'combat'; });
  // Always at least one fight per floor.
  if (!kinds.includes('combat')) kinds[middle[middle.length - 1]] = 'combat';

  const links2 = links;
  const deck = r.shuffle(charts);
  let dealt = (n - 1) * 6;
  const deal = () => deck[dealt++ % deck.length];
  const rooms: Room[] = [];
  for (let id = 0; id < cells.length; id++) {
    const [gx, gy] = cells[id];
    const doors: Room['doors'] = {};
    for (const [a, b] of links2) {
      if (a !== id && b !== id) continue;
      const other = a === id ? b : a;
      const [ox, oy] = cells[other];
      const d = DIRS.find(d => gx + STEP[d][0] === ox && gy + STEP[d][1] === oy)!;
      doors[d] = { to: other, locked: b === goal };
    }
    // Rooms are filled again (new shape, new layout) until every check passes.
    let room: Room | null = null;
    for (let t = 0; t < 40 && !room; t++) room = makeRoom(id, gx, gy, kinds[id], doors, r, n, usable ? deal : null);
    if (!room) return null;
    rooms.push(room);
  }
  return { n, theme: THEMES[n - 1], rooms, start: 0 };
}

/** Tiles you can't put things on: the doors' entry tiles and the tiles in from those. */
function nearDoors(doors: Room['doors']): Set<string> {
  const s = new Set<string>();
  for (const d of Object.keys(doors) as Dir[]) {
    const [x, y] = ENTRY[d];
    s.add(k(x, y));
    const [dx, dy] = STEP[OPP[d]];
    s.add(k(x + dx, y + dy));
    s.add(k(x + dx + dy, y + dy + dx)); s.add(k(x + dx - dy, y + dy - dx));
    s.add(k(x + dy, y + dx)); s.add(k(x - dy, y - dx));
  }
  return s;
}

/** Which shapes each kind of room may take. Puzzle rooms keep their middle open. */
const SHAPES_FOR: Record<RoomKind, Shape[]> = {
  start: ['rect', 'cut', 'octa'], combat: ['cut', 'cross', 'octa', 'core', 'hall', 'rect'], key: ['cut', 'cross', 'octa', 'core', 'hall'],
  treasure: ['cross', 'octa', 'cut'], stairs: ['cut', 'octa', 'bevel'],
  verb: ['bevel', 'rect'], plates: ['bevel', 'cut', 'octa'], crystals: ['cut', 'octa', 'cross'], mirror: ['bevel', 'rect'], sun: ['bevel', 'rect'], boss: ['rect'],
};

function makeRoom(id: number, gx: number, gy: number, kind: RoomKind, doors: Room['doors'], r: Rng, n: number, deal: (() => Chart) | null): Room | null {
  const N = RW * RH;
  const room: Room = {
    id, gx, gy, kind, shape: r.pick(SHAPES_FOR[kind]), doors,
    wall: Array.from({ length: N }, (_, i) => { const x = i % RW, y = Math.floor(i / RW); return x === 0 || y === 0 || x === RW - 1 || y === RH - 1; }),
    h: new Array(N).fill(0), ramp: new Array(N).fill(null),
    enemies: [], pillars: [], pots: [], decor: [], chest: null, stairsUp: kind === 'start' && n > 1,
  };
  for (const d of Object.keys(doors) as Dir[]) room.wall[idx(...DOOR_AT[d])] = false;
  const busy = nearDoors(doors);
  const setWall = (x: number, y: number) => { if (!busy.has(k(x, y))) room.wall[idx(x, y)] = true; };
  const take = (x: number, y: number) => busy.add(k(x, y));
  const open = (x: number, y: number) => x >= 1 && x <= RW - 2 && y >= 1 && y <= RH - 2 && !room.wall[idx(x, y)] && !busy.has(k(x, y)) && !room.ramp[idx(x, y)];

  // ── The room's shape ──
  const corners = (w: number, h: number) => {
    for (let x = 1; x <= w; x++) for (let y = 1; y <= h; y++) { setWall(x, y); setWall(RW - 1 - x, y); setWall(x, RH - 1 - y); setWall(RW - 1 - x, RH - 1 - y); }
  };
  if (room.shape === 'bevel') corners(1, 1);
  if (room.shape === 'cut') corners(2, 2);
  if (room.shape === 'cross') corners(3, 3);
  if (room.shape === 'octa') for (let a = 1; a <= 3; a++) for (let b = 1; b <= 3; b++) if (a + b <= 4) { setWall(a, b); setWall(RW - 1 - a, b); setWall(a, RH - 1 - b); setWall(RW - 1 - a, RH - 1 - b); }
  if (room.shape === 'core') { const w = r.int(1, 2); for (let x = 7 - w; x <= 7 + w; x++) for (let y = 4; y <= 6; y++) setWall(x, y); }
  if (room.shape === 'hall') for (const x of [3, 5, 9, 11]) for (const y of [3, 7]) if (!busy.has(k(x, y))) room.pillars.push([x, y]);

  // ── The stairs: down to the next floor, or the ones you came down ──
  if (kind === 'stairs') {
    // A wide stairwell going down to the north: three ramps, then a landing in the dark.
    for (let y = 1; y <= 5; y++) { setWall(5, y); setWall(9, y); }
    for (let x = 5; x <= 9; x++) setWall(x, 1);
    for (let x = 6; x <= 8; x++) {
      room.h[idx(x, 5)] = -1; room.ramp[idx(x, 5)] = 's';
      room.h[idx(x, 4)] = -2; room.ramp[idx(x, 4)] = 's';
      room.h[idx(x, 3)] = -3; room.ramp[idx(x, 3)] = 's';
      room.h[idx(x, 2)] = -3;
      for (let y = 2; y <= 6; y++) take(x, y);
    }
  }
  if (room.stairsUp) for (let x = 6; x <= 8; x++) for (let y = 8; y <= 9; y++) { room.wall[idx(x, y)] = true; }
  if (kind === 'start') { take(7, 6); take(7, 7); take(6, 6); take(8, 6); }

  // ── Hills: a raised plateau with a ramp up, in fighting and treasure rooms ──
  // Half the key rooms hide the key behind a verb puzzle (those stay flat).
  const keyVerb = kind === 'key' && !!deal && r.next() < 0.5;
  let plateau: [number, number][] = [];
  if (['combat', 'key', 'treasure', 'start'].includes(kind) && !keyVerb && room.shape !== 'core' && r.next() < 0.75) plateau = hill(room, r, busy);

  // ── Scenery along the walls (never solid) ──
  const edge: [number, number][] = [];
  for (let x = 1; x < RW - 1; x++) for (let y = 1; y < RH - 1; y++) {
    if (room.wall[idx(x, y)] || room.ramp[idx(x, y)] || busy.has(k(x, y))) continue;
    if (DIRS.some(d => room.wall[idx(x + STEP[d][0], y + STEP[d][1])])) edge.push([x, y]);
  }

  const spot = (x0 = 2, x1 = RW - 3, y0 = 2, y1 = RH - 3, o: { pad?: boolean; flat?: boolean; on?: [number, number][] } = {}): [number, number] | null => {
    for (let t = 0; t < 300; t++) {
      const [x, y] = o.on?.length ? r.pick(o.on) : [r.int(x0, x1), r.int(y0, y1)];
      if (!open(x, y) || room.pillars.some(p => p[0] === x && p[1] === y)) continue;
      if (o.flat && room.h[idx(x, y)] !== 0) continue;
      if (o.pad && [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]].some(([a, b]) => busy.has(k(x + a, y + b)) || room.wall[idx(x + a, y + b)])) continue;
      take(x, y); return [x, y];
    }
    return null;
  };
  const enemies = (count: number, kinds: EnemyKind[], on?: [number, number][]) => {
    for (let i = 0; i < count; i++) { const s = spot(2, RW - 3, 2, RH - 3, { on }); if (s) room.enemies.push({ kind: r.pick(kinds), x: s[0] + 0.5, y: s[1] + 0.5 }); }
  };
  /** An eye sentry: up on the hill if there is one, else somewhere along a wall. */
  const eye = (where?: [number, number][]) => {
    const s = spot(1, RW - 2, 1, RH - 2, { on: where ?? (plateau.length ? plateau : edge) });
    if (s) room.enemies.push({ kind: 'eye', x: s[0] + 0.5, y: s[1] + 0.5 });
  };
  const pots = (count: number, on?: [number, number][]) => { for (let i = 0; i < count; i++) { const s = spot(1, RW - 2, 1, RH - 2, { on }); if (s) room.pots.push(s); } };
  const pool: EnemyKind[] = n === 1 ? ['crab', 'crab', 'jelly'] : n === 2 ? ['crab', 'jelly', 'puffer', 'urchin'] : ['crab', 'puffer', 'urchin', 'jelly'];

  switch (kind) {
    case 'start': pots(2); break;
    case 'combat': case 'key': {
      if (room.shape !== 'hall') for (let i = 0; i < r.int(0, 2); i++) {
        const x = r.int(3, 5), y = r.int(3, RH - 4);
        for (const p of [[x, y], [RW - 1 - x, y]] as [number, number][]) if (open(...p) && room.h[idx(...p)] === 0) { take(...p); room.pillars.push(p); }
      }
      if (n >= 2 || plateau.length) eye();
      if (n >= 3 && r.next() < 0.5) eye(edge);
      enemies(2 + n + (kind === 'key' ? 0 : 1), pool);
      pots(2);
      if (kind === 'key') {
        if (keyVerb && deal) {
          // Half the time the key is behind a verb puzzle, with one guard.
          room.enemies = room.enemies.filter(e => e.kind === 'eye').slice(0, 1).concat(room.enemies.filter(e => e.kind !== 'eye').slice(0, 1));
          room.chest = { x: 7, y: 8, item: 'key', hidden: true };
          const v = verbPuzzle(room, r, n, deal(), busy);
          if (!v) return null;
          room.verb = v;
        } else {
          const s = spot(3, RW - 4, 3, RH - 4, { on: plateau.length ? plateau : undefined });
          if (!s) return null;
          room.chest = { x: s[0], y: s[1], item: 'key', hidden: true };
        }
      }
      break;
    }
    case 'verb': {
      const v = verbPuzzle(room, r, n, deal ? deal() : null, busy);
      if (!v) return null;
      room.verb = v;
      if (n >= 2) eye([[1, 1], [RW - 2, 1], [1, RH - 2], [RW - 2, RH - 2]].filter(([x, y]) => !room.wall[idx(x, y)]) as [number, number][]);
      if (n >= 3) enemies(1, ['jelly']);
      break;
    }
    case 'plates': {
      const chart = deal!();
      const pairs = r.shuffle(distinct(chart)).slice(0, Math.min(3 + n, 6));
      const plates: PlatesPuzzle['plates'] = [];
      take(7, 2);
      for (const p of r.shuffle(pairs)) { const s = spot(2, RW - 3, 4, RH - 3, { pad: true, flat: true }); if (s) plates.push({ x: s[0], y: s[1], form: p.form }); }
      room.plates = { chart, order: pairs.filter(p => plates.some(q => q.form === p.form)), plates };
      if (room.plates.order.length < 3) return null;
      if (n >= 3) eye(edge);
      break;
    }
    case 'crystals': {
      const spots: [number, number][] = [[4, 4], [10, 4], [4, 7], [10, 7]];
      if (spots.some(s => !open(...s))) return null;
      spots.forEach(s => take(...s)); take(7, 2);
      room.crystals = { rounds: rounds(r, 2 + n, deal), spots };
      if (n >= 2) enemies(1, ['jelly']);
      break;
    }
    case 'mirror': {
      const m = mirrorPuzzle(room, r, deal, busy);
      if (!m) return null;
      room.mirror = m;
      if (n >= 2) enemies(1, ['jelly', 'crab']);
      break;
    }
    case 'sun': {
      // Sun plaques on the north wall, a sunbeam in the middle; the plaques' columns stay clear.
      const plaques = [3, 5, 9, 11];
      const shaft: [number, number] = [7, 6];
      for (const x of plaques) for (let y = 1; y <= RH - 2; y++) take(x, y);
      take(...shaft);
      room.sun = { shaft, plaques, rounds: rounds(r, 2 + n, deal) };
      if (n >= 2) eye([[1, RH - 2], [RW - 2, RH - 2]].filter(([x, y]) => !room.wall[idx(x, y)]) as [number, number][]);
      if (n >= 3) enemies(1, ['puffer']);
      break;
    }
    case 'treasure': {
      const s = plateau.length ? spot(0, 0, 0, 0, { on: plateau }) : spot(5, 9, 4, 6);
      if (!s) return null;
      room.chest = { x: s[0], y: s[1], item: 'heart', hidden: false };
      pots(3);
      break;
    }
    case 'stairs': pots(2); if (n >= 2) eye(edge); break;
    case 'boss': {
      // The Kraken's pool fills the north of the room; four pronoun crystals on the south side.
      const spots: [number, number][] = [[3, 6], [11, 6], [5, 8], [9, 8]];
      const charts: Chart[] = [];
      if (deal) for (let i = 0; i < 6; i++) charts.push(deal());
      room.boss = { charts, spots };
      room.chest = { x: 7, y: 6, item: 'crown', hidden: true };
      break;
    }
  }
  // Scenery along the walls, where nothing else is (never solid).
  for (const [x, y] of r.shuffle(edge).filter(([x, y]) => !busy.has(k(x, y))).slice(0, 8)) {
    room.decor.push({ kind: r.pick(['kelp', 'kelp', 'coral', 'coral', 'shell', 'anemone', 'rock'] as DecorKind[]), x: x + 0.5 + (r.next() - 0.5) * 0.3, y: y + 0.5 + (r.next() - 0.5) * 0.3, s: 0.7 + r.next() * 0.6, r: r.next() * Math.PI * 2 });
  }
  return checkRoom(room) ? room : null;
}

/** A raised plateau (one step up) with a two-wide ramp onto it. Returns its tiles, or [] if none fits. */
function hill(room: Room, r: Rng, busy: Set<string>): [number, number][] {
  for (let t = 0; t < 30; t++) {
    const w = r.int(3, 5), hh = r.int(2, 3);
    const x0 = r.int(1, RW - 1 - w), y0 = r.int(1, RH - 1 - hh);
    const tiles: [number, number][] = [];
    for (let x = x0; x < x0 + w; x++) for (let y = y0; y < y0 + hh; y++) tiles.push([x, y]);
    if (tiles.some(([x, y]) => room.wall[idx(x, y)] || busy.has(k(x, y)) || room.pillars.some(p => p[0] === x && p[1] === y))) continue;
    // The ramp: two tiles off one side, going up toward the plateau, with floor in front of it.
    const side = r.pick(DIRS);
    const ramp: [number, number][] = [];
    if (side === 'n' || side === 's') { const y = side === 'n' ? y0 - 1 : y0 + hh; const x = r.int(x0, x0 + w - 2); ramp.push([x, y], [x + 1, y]); }
    else { const x = side === 'w' ? x0 - 1 : x0 + w; const y = r.int(y0, y0 + hh - 2); ramp.push([x, y], [x, y + 1]); }
    const [fx, fy] = STEP[side];
    const pillar = (x: number, y: number) => room.pillars.some(p => p[0] === x && p[1] === y);
    if (ramp.some(([x, y]) => x < 1 || y < 1 || x > RW - 2 || y > RH - 2 || room.wall[idx(x, y)] || busy.has(k(x, y)) || pillar(x, y) || isWall(room, x + fx, y + fy) || busy.has(k(x + fx, y + fy)) || pillar(x + fx, y + fy))) continue;
    for (const [x, y] of tiles) room.h[idx(x, y)] = 1;
    for (const [x, y] of ramp) room.ramp[idx(x, y)] = OPP[side];
    // The ramp, and the floor in front of it, stay clear.
    ramp.forEach(([x, y]) => { busy.add(k(x, y)); busy.add(k(x + fx, y + fy)); });
    // Every door still has to reach every other one, and the plateau.
    if (checkReach(room)) return tiles;
    for (const [x, y] of tiles) room.h[idx(x, y)] = 0;
    for (const [x, y] of ramp) { room.ramp[idx(x, y)] = null; busy.delete(k(x, y)); busy.delete(k(x + fx, y + fy)); }
  }
  return [];
}

function rounds(r: Rng, count: number, deal: (() => Chart) | null): Round[] {
  const out: Round[] = [];
  for (let i = 0; i < count; i++) {
    if (!deal) {
      const shapes = r.shuffle(SHAPES).slice(0, 4);
      out.push({ chart: null, who: shapes[0], form: shapes[0], options: r.shuffle(shapes) });
      continue;
    }
    const chart = deal();
    const pairs = r.shuffle(distinct(chart));
    const right = pairs[0];
    out.push({ chart, who: right.who, form: right.form, options: r.shuffle(pairs.slice(0, 4).map(p => p.form)) });
  }
  return out;
}

/**
 * The lantern sits on the west side of row 3 or 7 and shines east. Three mirrors on that row
 * each let the light pass, or turn it north or south up its column; receivers wait at the
 * ends of those columns and at the far east of the row, so each one can be lit by some setting.
 */
function mirrorPuzzle(room: Room, r: Rng, deal: (() => Chart) | null, busy: Set<string>): MirrorPuzzle | null {
  const ys = r.pick([3, 7]);
  const cols = [4, 9, 11];
  const source: [number, number] = [2, ys];
  const ends: [number, number][] = [];
  for (const c of cols) ends.push([c, 1], [c, RH - 2]);
  ends.push([RW - 2, ys]);
  const receivers = r.shuffle(ends.filter(([x, y]) => !room.wall[idx(x, y)] && !busy.has(k(x, y)))).slice(0, 4);
  if (receivers.length < 3) return null;
  // The light's paths stay clear: the row, and each column from the row to its receivers.
  const path = new Set<string>();
  for (let x = 2; x <= RW - 2; x++) path.add(k(x, ys));
  for (const c of cols) for (let y = 1; y <= RH - 2; y++) path.add(k(c, y));
  if ([source, ...receivers, ...cols.map(c => [c, ys] as [number, number])].some(([x, y]) => room.wall[idx(x, y)])) return null;
  for (const [x, y] of receivers) {
    // Everything between the row and a receiver must be open floor.
    if (y !== ys) for (let yy = Math.min(y, ys); yy <= Math.max(y, ys); yy++) if (room.wall[idx(x, yy)]) return null;
    if (y === ys) for (let xx = 2; xx <= x; xx++) if (room.wall[idx(xx, ys)]) return null;
  }
  path.forEach(p => busy.add(p));
  busy.add(k(...source)); receivers.forEach(([x, y]) => busy.add(k(x, y)));
  const mirrors = cols.map(c => ({ x: c, y: ys, state: r.int(0, 2) }));
  return { source, mirrors, receivers, rounds: rounds(r, 3, deal).map(rd => ({ ...rd, options: rd.options.slice(0, receivers.length) })).map(rd => rd.options.includes(rd.form) ? rd : { ...rd, options: [rd.form, ...rd.options.slice(1)] }) };
}

/**
 * Holes in a row near the north wall; blocks scattered over the middle, never touching each other
 * or a wall. A hole takes any block with its pronoun's form (two people can share a form), so `key`
 * is the form. Placed again until the solver below can do it.
 */
function verbPuzzle(room: Room, r: Rng, n: number, chart: Chart | null, busy: Set<string>): VerbPuzzle | null {
  const want = Math.min(2 + n, 5);
  let pairs: { label: string; key: string }[];
  if (chart) pairs = r.shuffle(chart.pairs).slice(0, want).map(p => ({ label: p.who, key: p.form }));
  else pairs = r.shuffle(SHAPES).slice(0, want).map(s => ({ label: s, key: s }));
  const cols = pairs.length === 3 ? [4, 7, 10] : pairs.length === 4 ? [3, 6, 8, 11] : [3, 5, 7, 9, 11];
  const holes = r.shuffle(pairs).map((p, i) => ({ x: cols[i], y: 2, label: p.label, key: p.key }));
  if (holes.some(h => room.wall[idx(h.x, h.y)] || room.h[idx(h.x, h.y)] !== 0 || room.ramp[idx(h.x, h.y)])) return null;
  for (let attempt = 0; attempt < 30; attempt++) {
    const taken = new Set<string>(busy);
    const blocks: VerbPuzzle['blocks'] = [];
    for (const p of pairs) {
      for (let t = 0; t < 400; t++) {
        const x = r.int(2, RW - 3), y = r.int(5, RH - 3);
        if (taken.has(k(x, y)) || room.wall[idx(x, y)] || room.h[idx(x, y)] !== 0 || room.ramp[idx(x, y)]) continue;
        if ([[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]].some(([a, b]) => blocks.some(q => q.x === x + a && q.y === y + b) || room.wall[idx(x + a, y + b)])) continue;
        if (room.chest && room.chest.x === x && Math.abs(room.chest.y - y) <= 1) continue;
        taken.add(k(x, y)); blocks.push({ x, y, label: p.key, key: p.key });
        break;
      }
    }
    const v = { chart, holes, blocks };
    if (blocks.length === holes.length && solvable(room, v)) {
      holes.forEach(h => busy.add(k(h.x, h.y)));
      for (const b of blocks) busy.add(k(b.x, b.y));
      // Keep the floor between the holes and the blocks clear of anything else.
      for (let x = 2; x <= RW - 3; x++) for (let y = 1; y <= RH - 2; y++) busy.add(k(x, y));
      return v;
    }
  }
  return null;
}

/** Can every block be pushed into a hole? One block at a time (the rest stay put), greedily. */
export function solvable(room: Room, v: VerbPuzzle): boolean {
  const base = new Set<string>();
  for (let x = 0; x < RW; x++) for (let y = 0; y < RH; y++) if (room.wall[idx(x, y)] || room.h[idx(x, y)] !== 0 || room.ramp[idx(x, y)] || x === 0 || y === 0 || x === RW - 1 || y === RH - 1) base.add(k(x, y));
  room.pillars.forEach(p => base.add(k(...p)));
  room.pots.forEach(p => base.add(k(...p)));
  room.enemies.filter(e => e.kind === 'eye').forEach(e => base.add(k(Math.floor(e.x), Math.floor(e.y))));
  const doors = Object.keys(room.doors) as Dir[];
  let player: [number, number] = ENTRY[doors[0]];
  let blocks = v.blocks.map(b => ({ ...b }));
  const filled = new Set<number>();
  while (blocks.length) {
    let moved = false;
    for (const b of blocks) {
      const targets = v.holes.map((h, i) => ({ h, i })).filter(({ h, i }) => h.key === b.key && !filled.has(i));
      const solid = new Set(base);
      v.holes.forEach((h, i) => { if (!filled.has(i)) solid.add(k(h.x, h.y)); });
      blocks.forEach(o => { if (o !== b) solid.add(k(o.x, o.y)); });
      const seen = new Set([`${b.x},${b.y},${player}`]);
      const q: [number, number, number, number][] = [[b.x, b.y, player[0], player[1]]];
      let found: { at: [number, number]; i: number } | null = null;
      for (let qi = 0; qi < q.length && !found; qi++) {
        const [bx, by, px, py] = q[qi];
        for (const [a, c] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = px + a, ny = py + c;
          let nbx = bx, nby = by;
          if (nx === bx && ny === by) {
            nbx = bx + a; nby = by + c;
            const t = targets.find(({ h }) => h.x === nbx && h.y === nby);
            if (t) { found = { at: [bx, by], i: t.i }; break; }
            if (solid.has(k(nbx, nby))) continue;
          } else if (solid.has(k(nx, ny))) continue;
          const s = `${nbx},${nby},${nx},${ny}`;
          if (seen.has(s)) continue;
          seen.add(s); q.push([nbx, nby, nx, ny]);
        }
      }
      if (found) { filled.add(found.i); blocks = blocks.filter(o => o !== b); player = found.at; moved = true; break; }
    }
    if (!moved) return false;
  }
  return true;
}

// ── Checks ──────────────────────────────────────────────────────────────────

/** The tiles the player can stand on from `from`, by the real movement rules (things in the room count as solid). */
export function reachable(room: Room, from: [number, number], extraSolid: Set<string> = new Set()): Set<string> {
  const seen = new Set([k(...from)]);
  const q: [number, number][] = [from];
  while (q.length) {
    const [x, y] = q.pop()!;
    for (const d of DIRS) {
      const nx = x + STEP[d][0], ny = y + STEP[d][1];
      if (nx < 0 || ny < 0 || nx >= RW || ny >= RH) continue;
      const key = k(nx, ny);
      if (seen.has(key) || extraSolid.has(key) || !canStep(room, x, y, d, true)) continue;
      seen.add(key); q.push([nx, ny]);
    }
  }
  return seen;
}

/** Things you can't walk through. */
export function staticSolids(room: Room): Set<string> {
  const s = new Set<string>();
  room.pillars.forEach(p => s.add(k(...p)));
  room.pots.forEach(p => s.add(k(...p)));
  room.enemies.filter(e => e.kind === 'eye').forEach(e => s.add(k(Math.floor(e.x), Math.floor(e.y))));
  if (room.chest && !room.chest.hidden) s.add(k(room.chest.x, room.chest.y));
  room.crystals?.spots.forEach(p => s.add(k(...p)));
  room.boss?.spots.forEach(p => s.add(k(...p)));
  if (room.plates || room.crystals) s.add(k(7, 2));
  if (room.mirror) { s.add(k(...room.mirror.source)); room.mirror.mirrors.forEach(m => s.add(k(m.x, m.y))); room.mirror.receivers.forEach(p => s.add(k(...p))); }
  room.verb?.holes.forEach(h => s.add(k(h.x, h.y)));
  room.verb?.blocks.forEach(b => s.add(k(b.x, b.y)));
  if (room.kind === 'boss') for (let x = 3; x <= 11; x++) for (let y = 1; y <= 3; y++) s.add(k(x, y));
  return s;
}

/** Every door reaches every other door (going both ways). */
function checkReach(room: Room): boolean {
  const solid = staticSolids(room);
  const starts = (Object.keys(room.doors) as Dir[]).map(d => ENTRY[d]);
  if (room.kind === 'start') starts.push([7, 6]);
  for (const a of starts) {
    const seen = reachable(room, a, solid);
    if (starts.some(b => !seen.has(k(...b)))) return false;
  }
  return true;
}

const near = (seen: Set<string>, x: number, y: number) => DIRS.some(d => seen.has(k(x + STEP[d][0], y + STEP[d][1])));

/** Everything in the room can be reached and used, from every door. */
export function checkRoom(room: Room): boolean {
  if (!checkReach(room)) return false;
  const solid = staticSolids(room);
  const doors = Object.keys(room.doors) as Dir[];
  const from = room.kind === 'start' ? [7, 6] as [number, number] : ENTRY[doors[0]];
  const seen = reachable(room, from, solid);
  // Anything you have to touch, open, strike or stand on.
  if (room.chest && !near(seen, room.chest.x, room.chest.y) && !seen.has(k(room.chest.x, room.chest.y))) return false;
  if (room.plates?.plates.some(p => !seen.has(k(p.x, p.y)))) return false;
  if (room.crystals?.spots.some(([x, y]) => !near(seen, x, y))) return false;
  if (room.boss?.spots.some(([x, y]) => !near(seen, x, y))) return false;
  if (room.mirror?.mirrors.some(m => !near(seen, m.x, m.y))) return false;
  if (room.sun && !seen.has(k(...room.sun.shaft))) return false;
  if (room.sun?.plaques.some(x => !seen.has(k(x, 1)))) return false;
  if (room.kind === 'stairs' && !seen.has(k(7, 2))) return false;
  if (room.verb && !solvable(room, room.verb)) return false;
  // Enemies that walk must start somewhere you can get to (or they'd be stuck out of reach, and the doors shut for good).
  if (room.enemies.some(e => e.kind !== 'eye' && !seen.has(k(Math.floor(e.x), Math.floor(e.y))))) return false;
  if (room.enemies.some(e => e.kind === 'eye' && !near(seen, Math.floor(e.x), Math.floor(e.y)))) return false;
  return true;
}
